import axios from "axios";

/**
 * Shape of the FastAPI/our backend's structured error response. The backend
 * returns ``detail`` for the top-level message and an optional
 * ``upload_errors: string[]`` for per-file failures.
 */
export interface ServerErrorPayload {
    detail?: string;
    upload_errors?: string[];
}

/**
 * Pull the structured ``{ detail, upload_errors }`` shape off an axios error
 * response. Returns ``null`` when the error is not from axios, has no
 * response body, or carries neither field. Use this when you want to
 * distinguish per-file failures from a top-level error message.
 */
export function getServerErrorPayload(error: unknown): ServerErrorPayload | null {
    if (!axios.isAxiosError(error)) return null;
    const data = error.response?.data;
    if (!data || typeof data !== "object") return null;
    const payload: ServerErrorPayload = {};
    const maybeDetail = (data as Record<string, unknown>).detail;
    if (typeof maybeDetail === "string") payload.detail = maybeDetail;
    // A validation endpoint answers with every reason at once, so ``detail``
    // can be a list of messages. Dropping it left the caller with axios's own
    // "Request failed with status code 422", which tells the user nothing
    // about what to fix.
    else if (Array.isArray(maybeDetail)) {
        const messages = maybeDetail.filter((item): item is string => typeof item === "string");
        if (messages.length) payload.detail = messages.join(". ");
    }
    const maybeErrors = (data as Record<string, unknown>).upload_errors;
    if (Array.isArray(maybeErrors)) {
        payload.upload_errors = maybeErrors.filter((e): e is string => typeof e === "string");
    }
    return payload.detail || payload.upload_errors?.length ? payload : null;
}

/**
 * Machine-readable error marker some backend errors carry alongside the prose
 * ``detail`` (e.g. ``sharepoint-grant-required`` on the SharePoint connect 502,
 * PP-1097). Returns ``null`` when the error is not from axios or has no marker.
 */
export function getServerErrorCode(error: unknown): string | null {
    if (!axios.isAxiosError(error)) return null;
    const code = error.response?.headers?.["x-primepoint-error-code"];
    return typeof code === "string" && code ? code : null;
}

/**
 * HTTP status of a failed request, when the failure reached the server at all.
 * Returns ``null`` for anything else: a network error, an aborted request and a
 * thrown string never got a verdict, so they must not be read as one.
 */
export function getErrorStatus(error: unknown): number | null {
    if (!axios.isAxiosError(error)) return null;
    const status = error.response?.status;
    return typeof status === "number" ? status : null;
}

/**
 * Pluck a user-displayable error message off an unknown thrown value, with a
 * fallback. Tries ``error.response.data.detail`` first, then ``error.message``,
 * then the supplied fallback. Use this for the common "show the server's
 * message in a snackbar/alert" case — most call sites don't care about
 * structured per-file errors.
 */
export function getErrorDetail(error: unknown, fallback: string): string {
    const payload = getServerErrorPayload(error);
    if (payload?.detail) return payload.detail;
    if (error instanceof Error && error.message) return error.message;
    return fallback;
}
